import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ListView, { ListViewItem } from '@ingka/list-view';

import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody } from '@ingka/modal';
import { useUserRoles } from '@/redux/features/session/hooks';
import { useContentTracking } from '@/lib/hooks/tracking';
import { useModalGoToProfile } from '@/lib/hooks/modal';

import styles from './index.module.css';

const ROLES_SCOPES = [
  {
    prefix: 'PLATFORM_OWNER',
    i18nKey: 'label.roleScopes.platformOwner',
  },
  {
    prefix: 'LIG',
    i18nKey: 'label.roleScopes.tenant',
  },
  {
    prefix: 'CON',
    i18nKey: 'label.roleScopes.continent',
  },
  {
    prefix: 'CTY',
    i18nKey: 'label.roleScopes.country',
  },
  {
    prefix: 'STO',
    i18nKey: 'label.roleScopes.store',
  },
];

const UserRoleListItem: FC<{
  scope: string;
}> = ({ scope }) => {
  const { t } = useTranslation();
  const chunks = scope.split('.'); // CTY.IL - NORTHEN_BIRCH.CTY.IL - STO.009
  const region = chunks[chunks.length - 1];
  const client = chunks[chunks.length - 2] || chunks[chunks.length - 1]; // PLATFORM_OWNER
  const { i18nKey } =
    ROLES_SCOPES.find(({ prefix }) => client.startsWith(prefix)) || {};

  if (!i18nKey) return null;

  return (
    <ListViewItem
      className={styles['small']}
      inset
      showDivider={false}
      title={t(i18nKey)}
      quantityLabel={region}
      size="small"
    />
  );
};

const PermissionGroup: FC<{
  name: string;
  description?: string;
  scopes?: string[];
}> = ({ name, description, scopes }) => (
  <div>
    <h3 className="pb-2 pt-6">
      <b>{name}</b>
    </h3>
    {description && <p>{description}</p>}
    {scopes?.map((scope) => (
      <UserRoleListItem key={scope} scope={scope} />
    ))}
  </div>
);

const PermissionsSider: FC<ModalComponentBaseProps> = ({
  handleDismiss,
  ...rest
}) => {
  // Hooks
  const trackContent = useContentTracking();
  const { t } = useTranslation();
  const roles = useUserRoles();
  const groupedRoles = useMemo(
    () =>
      roles.reduce((acc, role) => {
        acc[role.name] ||= {
          name: role.name,
          description: role.description,
          scopes: [],
        };
        acc[role.name].scopes.push(role.clientReference);
        return acc;
      }, {} as Record<string, { name: string; description: string; scopes: string[] }>),
    [roles]
  );
  const hasPermissions = Object.values(groupedRoles).length > 0;

  useEffect(() => {
    trackContent('permissions', { type: 'sider' });
  }, []);

  const goBack = useModalGoToProfile();

  return (
    <Sheets
      {...rest}
      data-testid="myPermissionsModal"
      aria-label={t('page.permissions.title')}
      footer={null}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('page.permissions.title')}
          backBtnClick={goBack}
        />
      }
    >
      <ModalBody>
        <p className="mb-4">{t('page.permissions.description')}</p>

        <ListView data-testid="permissions" id="permissions">
          {hasPermissions ? (
            Object.values(groupedRoles).map(({ name, description, scopes }) => (
              <PermissionGroup
                key={name}
                name={name}
                description={description}
                scopes={scopes}
              />
            ))
          ) : (
            <PermissionGroup name={t('label.viewerOnly')} />
          )}
        </ListView>
      </ModalBody>
    </Sheets>
  );
};

export default PermissionsSider;
